// Read-only field-mapping table for the Integration detail shell's visual
// Mapping preview (roadmap #9 Phase 1, a scoped-down first cut of slice 6 -
// see docs/INTEGRATION_TOOL_PHASE0_AUDIT.md §6.6.7's slice table). Turns
// spec.rules/spec.outputs into rows a user can read without opening the raw
// JSON - purely descriptive, no evaluation: mappingEngine.ts/mappingPreview.ts
// remain the only place a mapping's actual behavior is computed.
import type { MappingRule, MappingOutputSpec, MappingSpec } from './mappingEngine';

export interface MappingFieldRow {
  to: string;
  source: string;
  required: boolean;
  transform: string;
}

export interface MappingOutputRows {
  outputIndex: number;
  rows: MappingFieldRow[];
}

function describeSource(rule: MappingRule): string {
  if (rule.from) return `path: ${rule.from}`;
  if (rule.const !== undefined) return `const: ${JSON.stringify(rule.const)}`;
  if (rule.default !== undefined) return `default: ${JSON.stringify(rule.default)}`;
  return '—';
}

function describeTransform(rule: MappingRule): string {
  const parts: string[] = [];
  if (rule.map && typeof rule.map === 'object' && !Array.isArray(rule.map)) {
    parts.push(`map (${Object.keys(rule.map).length} value${Object.keys(rule.map).length === 1 ? '' : 's'})`);
  }
  if (rule.cast) parts.push(`cast → ${rule.cast}`);
  if (typeof rule.unitScale === 'number') parts.push(`× ${rule.unitScale}`);
  if (Array.isArray(rule.ops) && rule.ops.length > 0) {
    const sym: Record<string, string> = { '/': '÷', '*': '×', '+': '+', '-': '−' };
    parts.push(rule.ops.map((o) => `${sym[o.op] ?? o.op} ${o.from ? o.from : o.value ?? '?'}`).join(' '));
  }
  if (rule.timestamp) parts.push(`timestamp (${rule.timestamp})`);
  return parts.join(', ');
}

function describeRule(rule: MappingRule): MappingFieldRow {
  return {
    to: rule.to ?? '',
    source: describeSource(rule),
    required: !!rule.required,
    transform: describeTransform(rule),
  };
}

// Mirrors applyOneOutput's own iteration in mappingEngine.ts: one row per
// rule that names a `to` target, in the same output-list shape (spec.outputs,
// or one implicit output from spec.rules) the engine itself evaluates.
export function describeMappingRules(spec: MappingSpec | undefined): MappingOutputRows[] {
  const outputDefs: MappingOutputSpec[] = Array.isArray(spec?.outputs)
    ? (spec!.outputs as MappingOutputSpec[])
    : [{ rules: spec?.rules }];

  return outputDefs.map((def, outputIndex) => ({
    outputIndex,
    rows: (def?.rules ?? []).filter((r): r is MappingRule => !!r?.to).map(describeRule),
  }));
}
